import * as AuthApi from "Data/API/Auth";
import * as UserApi from "Data/API/User";
import type {
	LoginUserResponse, UpdatePasswordPayload, UpdateUserPayload, User, WithApiData, UserState,
	UserResource, CreateUserHostnamePayload, ApiHostname, UpdateAccountPayload
} from "Interfaces";
import { createAppSlice } from "Data/Redux/Helpers";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, constant } from "lodash";
import { handleClearCache } from "Data/Utils/Redux";
import { clearCacheAction } from "Data/Actions/System";
import { createIdentitySelector, sanitizeEmptyStrings } from "@clintonelec/typescriptutils";

const initialState: UserState = {
	hostnameLimit: -1,
	pusherKey: null,
	token: null,
	user: null,
	users: []
};

// # region Reducers

export const loginUserReducer = (state: UserState, action: PayloadAction<LoginUserResponse>) => {
	const { pusherKey, user, token, hostnameLimit } = action?.payload ?? {};

	if (!token) {
		return;
	}

	state.pusherKey = pusherKey;
	state.user = user;
	state.token = token;
	state.hostnameLimit = hostnameLimit;
};

export const updateAccountReducer = (state: UserState, action: PayloadAction<User>) => {
	state.user = {
		...state.user,
		...action.payload
	};
};

export const resetUsersReducer = (state: UserState) => {
	state.users = [];
};

// #endregion

// # region

export const getUserState = (state: UserState) => state;

export const getUser = (state: UserState) => {
	return state.user;
};

export const getToken = (state: UserState) => {
	return state.token;
};

export const getPusherKey = (state: UserState) => {
	return state.pusherKey;
};

export const getHostnameLimit = (state: UserState) => {
	return state.hostnameLimit;
};

export const getUsers = (state: UserState) => {
	return state.users;
};

export const getUserHostnames = (state: UserState, userId: string) => {
	const foundUser = state.users.find(user => user.id === userId);

	return foundUser?.hostnames ?? [];
};

export const getUserHostnamesMemoized = createSelector(
	[
		getUserState,
		createIdentitySelector<UserState, string>()
	],
	getUserHostnames
);

// # endregion

const userSlice = createAppSlice({
	name: "user",
	initialState,
	reducers: (builder) => ({
		registerUserAction: builder.asyncThunk(AuthApi.registerUser),
		logoutUserAction: builder.reducer(constant(initialState)),
		updateAccountPusherAction: builder.reducer(updateAccountReducer),
		loginUserAction: builder.asyncThunk(AuthApi.loginUser, {
			fulfilled: loginUserReducer
		}),
		updateUserAccountAction: builder.asyncThunk(
			(payload: UpdateAccountPayload, thunkApi): Promise<User> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };

				return UserApi.updateAccount(payload, token ?? "");
			}, {
				fulfilled: (state, action: PayloadAction<User>) =>
					updateAccountReducer(state, action)
			}
		),
		deleteUserAccountAction: builder.asyncThunk(
			(payload: void, thunkApi): Promise<WithApiData<void>> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };

				return UserApi.deleteAccount(token ?? "");
			}, {
				fulfilled: constant(cloneDeep(initialState))
			}
		),
		updateAccountPasswordAction: builder.asyncThunk(
			(payload: UpdatePasswordPayload, thunkApi): Promise<WithApiData<unknown>> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };

				return UserApi.resetAccountPassword(payload, token ?? "");
			}
		),
		fetchUsersAction: builder.asyncThunk(
			(payload: Record<string, string>, thunkApi): Promise<UserResource[]> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };
				const params: Record<string, string> = {
					...payload,
					"include": "hostnames"
				};

				return UserApi.fetchUsers(params, token ?? "");
			}, {
				fulfilled: (state, action) => {
					state.users = action.payload;
				}
			}
		),
		resetUsersAction: builder.reducer(resetUsersReducer),
		updateUserAction: builder.asyncThunk(
			(payload: UpdateUserPayload, thunkApi): Promise<User> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };

				return UserApi.updateUser(payload, token ?? "");
			}, {
				fulfilled: (state, action) => {
					const { payload } = action;
					const foundIndex = state.users.findIndex(user => user.id === payload.id);

					if (foundIndex < 0) {
						return;
					}

					state.users[ foundIndex ] = {
						...state.users[ foundIndex ],
						...payload
					};
				}
			}
		),
		deleteUserAction: builder.asyncThunk(
			(payload: string, thunkApi): Promise<string> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };

				return UserApi.deleteUser(payload, token ?? "")
					.then(() => payload);
			}, {
				fulfilled: (state, action) => {
					const { payload } = action;
					const filteredUsers = state.users.filter(user => user.id !== payload);

					state.users = filteredUsers;
				}
			}
		),
		createUserHostnameAction: builder.asyncThunk(
			(payload: CreateUserHostnamePayload, thunkApi): Promise<ApiHostname & { userId: string }> => {
				const { user: { token } } = thunkApi.getState() as { user: UserState };
				const sanitizedPayload = sanitizeEmptyStrings(payload) as CreateUserHostnamePayload;

				return UserApi.createUserHostname(sanitizedPayload, token ?? "")
					.then(
						hostname => ({ ...hostname, userId: payload.userId })
					);
			}, {
				fulfilled: (state, action) => {
					const { payload } = action;
					const foundIndex = state.users.findIndex(user => user.id === payload.id);

					if (foundIndex < 0) {
						return;
					}

					state.users[ foundIndex ].hostnames = [
						...state.users[ foundIndex ].hostnames,
						payload
					];
				}
			}
		)
	}),
	selectors: {
		selectUser: getUser,
		selectToken: getToken,
		selectPusherKey: getPusherKey,
		selectHostnameLimit: getHostnameLimit,
		selectUsers: getUsers,
		selectUserHostnames: getUserHostnamesMemoized
	},
	extraReducers: (builder) => {
		builder.addCase(clearCacheAction, handleClearCache(initialState));
	}
});

export default userSlice;

export const {
	deleteUserAccountAction, loginUserAction, logoutUserAction, registerUserAction, updateAccountPusherAction,
	updateUserAccountAction, updateAccountPasswordAction, fetchUsersAction, resetUsersAction, createUserHostnameAction,
	deleteUserAction, updateUserAction
} = userSlice.actions;

export const {
	selectHostnameLimit, selectPusherKey, selectToken, selectUser, selectUsers, selectUserHostnames
} = userSlice.selectors;
